/**
 * buildAdvisorFromReports
 * ------------------------
 * Adapter qui transforme les vraies données utilisateur (Redux user +
 * MonBilanPatrimonialReports) en données pour le ConseillerPatrimonialPage.
 *
 * Plus de mock. Le score, le diagnostic, les actions prioritaires et la
 * projection 20 ans sont calculés côté front à partir des chiffres réels
 * de l'utilisateur connecté. Quand le backend exposera des endpoints LLM
 * dédiés (compute-score, generate-advice), ils remplaceront ce calcul.
 *
 * LAP-i18n — Toutes les chaînes affichées sont maintenant produites via la
 * fonction de traduction `t` injectée par le composant React. Les composants
 * recomputent ce builder lorsque `i18n.language` change.
 *
 * Référence règles métier : utils/scoringEngine.js
 */

import {
  scoreToGrade,
  scoreEpargnePrecaution,
  scoreDiversification,
  scoreEndettement,
  scorePrevoyance,
  scoreFiscalite,
  scoreObjectifs,
  computeGlobalScore,
} from './scoringEngine'
import { getCountryProfile } from './countryProfiles'

// Mapping des libellés de types de compte Django (AccounType.TypeCompte, cf.
// Life_backend/utils/DefaultData/AccounType.csv) vers nos clés internes.
// IMPORTANT : ce mapping DOIT couvrir TOUS les types de compte réels, sinon
// l'advisor devient aveugle aux comptes existants de l'utilisateur (ex. il
// recommandait d'« ouvrir un Livret A » alors que l'user en a un, ou affichait
// le risque « pas de retraite » alors que l'Épargne Retraite est sa plus grosse
// poche). Tout libellé non listé retombe sur slugify() — d'où l'importance de
// garder les clés ci-dessous alignées avec le slug naturel.
const ASSET_LABELS = {
  // — Liquidités / comptes courants —
  'Compte courant': 'compte_courant',
  'Compte joint': 'compte_joint',
  // — Livrets & épargne de précaution réglementée —
  'Livret A': 'livret_a',
  Livret: 'livret_a',
  LDDS: 'ldds',
  LEP: 'lep',
  'Livret jeune': 'livret_jeune',
  CEL: 'cel',
  PEL: 'pel',
  'Compte épargné': 'compte_epargne',
  'Compte épargne': 'compte_epargne',
  'Compte à terme': 'compte_a_terme',
  // — Assurance vie (toutes orthographes + libellé EN backend) —
  'Assurance vie': 'assurance_vie',
  'Assurance Vie': 'assurance_vie',
  'Assurance-vie': 'assurance_vie',
  AV: 'assurance_vie',
  'Life insurance': 'assurance_vie',
  // — Épargne retraite (famille PER) —
  PER: 'per',
  PERCO: 'perco',
  PERP: 'perp',
  'Epargne Retraite': 'epargne_retraite',
  'Épargne Retraite': 'epargne_retraite',
  // — Marchés financiers —
  PEA: 'pea',
  'PEA PME': 'pea_pme',
  'PEA-PME': 'pea_pme',
  PEE: 'pee',
  'Compte titre': 'compte_titre',
  Actions: 'actions',
  Obligations: 'obligations',
  Crypto: 'crypto',
  // — Immobilier & divers —
  Immobilier: 'immobilier',
  'Immobilier locatif': 'immobilier',
  'Résidence principale': 'immobilier',
  'Bien matériel': 'bien_materiel',
}

// ─── Regroupements métier ─────────────────────────────────────────────
// Plusieurs véhicules couvrent le même besoin patrimonial. On raisonne par
// GROUPE (et plus par clé unique « per »/« assurance_vie ») pour que la
// détection « l'user a-t-il déjà X ? » soit correcte quel que soit le véhicule.

// Épargne retraite : tout produit retraite compte comme « prévoyance retraite ».
const RETIREMENT_KEYS = ['per', 'perco', 'perp', 'epargne_retraite']
// Assurance vie.
const AV_KEYS = ['assurance_vie']
// Livrets réglementés (épargne de précaution « cœur »).
const LIVRET_KEYS = ['livret_a', 'ldds', 'lep', 'livret_jeune', 'cel']
// Comptes exposés aux marchés actions.
const MARKET_KEYS = ['pea', 'pea_pme', 'pee', 'compte_titre', 'actions', 'obligations']
// Liquidités immédiatement disponibles pour l'épargne de précaution :
// comptes courants + livrets + comptes d'épargne libres. On EXCLUT les
// produits bloqués/à échéance (PEL, compte à terme) qui ne sont pas
// mobilisables sans pénalité en cas de coup dur.
const SAFE_LIQUIDITY_KEYS = [
  'compte_courant',
  'compte_joint',
  'compte_epargne',
  ...LIVRET_KEYS,
]

// Somme des montants ABSOLUS (€) pour un ensemble de clés internes.
function sumAbsolute(repartition, keys) {
  const abs = (repartition && repartition._absolute) || {}
  return keys.reduce((s, k) => s + (abs[k] || 0), 0)
}

// L'utilisateur détient-il AU MOINS un véhicule du groupe (montant > 0) ?
function hasAny(repartition, keys) {
  return sumAbsolute(repartition, keys) > 0
}

// LAP-i18n — Identity fallback used when the page is rendered before a real
// t function is provided. Returns the defaultValue when present.
const identityT = (key, opts = {}) => {
  if (opts && typeof opts.defaultValue === 'string') return opts.defaultValue
  return key
}

export function buildAdvisorFromReports({ reports = [], user = {}, t = identityT } = {}) {
  // ─────── Pays de résidence & devise (moteur multi-pays) ───────
  // Avant : tout le moteur était codé en dur FR/€. Désormais on dérive un
  // « profil pays » à partir de user.pays_id (résidence) et user.currency_code
  // (devise d'affichage), qui pilote le formatage monétaire, le barème fiscal
  // et le nom local des produits recommandés.
  const profile = getCountryProfile({
    paysId: user.pays_id || user.paysId || user.country || user.pays,
    currencyCode:
      user.currency_code || user.currencyCode || user.currency || undefined,
  })
  const money = profile.formatMoney
  const products = {
    precaution: profile.productName('precaution', t),
    retirement: profile.productName('retirement', t),
    lifeInsurance: profile.productName('lifeInsurance', t),
    equity: profile.productName('equity', t),
  }

  // ─────── Extraction des sections ───────
  const reportByType = {}
  reports.forEach((r) => {
    if (r?.type) reportByType[r.type] = r.data
  })

  const portrait = reportByType['mon-portrait'] || {}
  const profil = reportByType['profil-investisseure'] || {}
  const budget = reportByType['mon-budget'] || {}
  const patrimoine = reportByType['mon-patrimoine'] || {}
  const patrimoineNet = reportByType['patrimoine-net'] || {}
  const objectifsRaw = reportByType['mes-objectifs'] || []

  const firstName =
    user.first_name || user.firstName || user.prenom || portrait.prenom || ''
  const lastName = user.last_name || user.nom || portrait.nom || ''
  const ageRaw =
    user.age ||
    portrait.age ||
    computeAgeFromBirthdate(user.date_de_naissance || portrait.date_de_naissance)
  const age = parseInt(ageRaw, 10) || null
  const city = user.vile || portrait.vile || ''
  const children = parseInt(user.nombre_enfants ?? portrait.nombre_enfants, 10) || 0

  // ─────── Budget / cashflow ───────
  const monthlyIncome = num(budget.avarage_income, 0)
  const monthlyExpenses = num(budget.depense, 0)
  const monthlyTaxes = num(budget.impots, 0)
  const monthlyCashflow = num(
    budget.cashflow,
    monthlyIncome - monthlyExpenses - monthlyTaxes,
  )
  const lastTaxRate = num(budget.last_tax_rate, null)

  // ─────── Patrimoine ───────
  const actifs = patrimoine.actifs || { labels: [], data: [], total: 0 }
  const passifs = patrimoine.passifs || { labels: [], data: [], total: 0 }
  const totalActifs = num(actifs.total, sum(actifs.data))
  const totalPassifs = num(passifs.total, sum(passifs.data))
  const patrimoineNetValue = num(patrimoineNet.total, totalActifs - totalPassifs)

  // Répartition normalisée (clés internes)
  const repartition = buildRepartition(actifs.labels, actifs.data, totalActifs)

  const liquiditesDisponibles = SAFE_LIQUIDITY_KEYS.reduce(
    (acc, k) => acc + (repartition._absolute?.[k] || 0),
    0,
  )

  // ─────── Objectifs ───────
  const objectifsDefinis = Array.isArray(objectifsRaw) ? objectifsRaw : []

  // ─────── Profil investisseur ───────
  const risqueScore = num(profil.risque_score, null)
  const financiereScore = num(profil.financiere_score, null)
  const financeInterests = Array.isArray(profil.finance_interest)
    ? profil.finance_interest
    : []

  // ─────── Dimensions du score ───────
  const dimensions = [
    {
      key: 'epargne',
      label: t('conseillerPatrimonial.dimensions.epargne'),
      weight: 0.2,
      score: scoreEpargnePrecaution({
        liquiditesDisponibles,
        depensesMensuelles: monthlyExpenses,
      }),
      detail: epargneDetail(t, liquiditesDisponibles, monthlyExpenses, products.precaution),
      status: statusForScore(
        scoreEpargnePrecaution({
          liquiditesDisponibles,
          depensesMensuelles: monthlyExpenses,
        }),
      ),
    },
    {
      key: 'diversification',
      label: t('conseillerPatrimonial.dimensions.diversification'),
      weight: 0.2,
      score: scoreDiversificationFromRepartition(repartition),
      detail: diversificationDetail(t, repartition),
      status: statusForScore(scoreDiversificationFromRepartition(repartition)),
    },
    {
      key: 'endettement',
      label: t('conseillerPatrimonial.dimensions.endettement'),
      weight: 0.15,
      score: scoreEndettement({
        patrimoineNet: patrimoineNetValue,
        revenuMensuel: monthlyIncome,
        mensualitesCredits: 0, // TODO : remonter depuis backend si dispo
      }),
      detail: endettementDetail(t, money, patrimoineNetValue, monthlyCashflow),
      status: statusForScore(
        scoreEndettement({
          patrimoineNet: patrimoineNetValue,
          revenuMensuel: monthlyIncome,
          mensualitesCredits: 0,
        }),
      ),
    },
    {
      key: 'prevoyance',
      label: t('conseillerPatrimonial.dimensions.prevoyance'),
      weight: 0.2,
      score: scorePrevoyance({
        aPer: hasAny(repartition, RETIREMENT_KEYS),
        anciennetéAv: hasAny(repartition, AV_KEYS) ? 1 : 0,
        aPrevoyance: false, // TODO backend : exposer flag prévoyance
        age,
      }),
      detail: prevoyanceDetail(t, repartition, age, products),
      status: statusForScore(
        scorePrevoyance({
          aPer: (repartition._absolute?.per || 0) > 0,
          anciennetéAv: (repartition._absolute?.assurance_vie || 0) > 0 ? 1 : 0,
          aPrevoyance: false,
          age,
        }),
      ),
    },
    {
      key: 'fiscalite',
      label: t('conseillerPatrimonial.dimensions.fiscalite'),
      weight: 0.15,
      score: scoreFiscaliteFromTaxRate({
        lastTaxRate,
        annualIncome: monthlyIncome * 12,
        hasPer: hasAny(repartition, RETIREMENT_KEYS),
        profile,
      }),
      detail: fiscaliteDetail(t, {
        lastTaxRate,
        annualIncome: monthlyIncome * 12,
        hasPer: hasAny(repartition, RETIREMENT_KEYS),
        profile,
        products,
      }),
      status: statusForScore(
        scoreFiscaliteFromTaxRate({
          lastTaxRate,
          annualIncome: monthlyIncome * 12,
          hasPer: hasAny(repartition, RETIREMENT_KEYS),
          profile,
        }),
      ),
    },
    {
      key: 'objectifs',
      label: t('conseillerPatrimonial.dimensions.objectifs'),
      weight: 0.1,
      score: scoreObjectifs({
        objectifsDefinis,
        repartitionVsCible: 0.5,
      }),
      detail: objectifsDetail(t, objectifsDefinis),
      status: statusForScore(
        scoreObjectifs({ objectifsDefinis, repartitionVsCible: 0.5 }),
      ),
    },
  ]

  const globalScore = computeGlobalScore(dimensions)
  const grade = scoreToGrade(globalScore)

  // ─────── Forces / Faiblesses / Risques ───────
  const diagnostic = buildDiagnostic({
    t,
    money,
    age,
    monthlyCashflow,
    monthlyIncome,
    patrimoineNetValue,
    repartition,
    dimensions,
    liquiditesDisponibles,
    monthlyExpenses,
  })

  // ─────── Actions prioritaires ───────
  const actionsPrioritaires = buildActionsPrioritaires({
    t,
    money,
    profile,
    products,
    age,
    monthlyIncome,
    monthlyCashflow,
    lastTaxRate,
    repartition,
    liquiditesDisponibles,
    monthlyExpenses,
    objectifsDefinis,
    patrimoineNetValue,
    city,
  })

  // ─────── Projets créables ───────
  const projetsSugeres = buildProjets({
    t,
    money,
    profile,
    products,
    monthlyCashflow,
    repartition,
    age,
    monthlyExpenses,
    liquiditesDisponibles,
    city,
  })

  // ─────── Projection 20 ans ───────
  const projection20ans = buildProjection20ans({
    patrimoineNetValue,
    monthlyCashflow,
  })

  // ─────── Plans vers les objectifs définis (LAP-CP08) ───────
  // Pour CHAQUE objectif exploitable défini dans Mes Objectifs (patrimoine,
  // revenu, cashflow, revenu passif, rendement), on calcule un plan dédié.
  // Tableau vide si aucun objectif exploitable → la section ne s'affiche pas.
  const objectivePlans = buildObjectivePlans({
    profile,
    objectifsDefinis,
    patrimoineNetValue,
    monthlyCashflow,
    monthlyIncome,
    profil,
  })

  // ─────── Suggested prompts (personnalisés au profil) ───────
  const suggestedPrompts = buildSuggestedPrompts({
    t,
    money,
    products,
    objectifsDefinis,
    age,
    repartition,
    monthlyCashflow,
  })

  // LAP-748 — Détection heuristique : le cashflow et les chiffres budget sont
  // "théoriques" (issus de la cible de répartition utilisateur) tant qu'aucune
  // opération bancaire n'est importée. Signaux qui indiquent du théorique :
  //  - aucun actif renseigné (totalActifs === 0)
  //  - aucun passif renseigné (totalPassifs === 0)
  //  - patrimoineNet quasi nul
  // Quand l'utilisateur importe ses opérations, le backend recalcule le
  // cashflow à partir des vraies transactions et ces conditions tombent.
  // TODO BACKEND : exposer un champ explicite `has_operations` ou
  // `is_cashflow_theoretical` dans la réponse `mon-budget` pour une
  // détection fiable au lieu d'heuristique.
  const isCashflowTheoretical =
    totalActifs === 0 && totalPassifs === 0 && Math.abs(patrimoineNetValue) < 100

  return {
    // Devise & formateur monétaire du profil pays. Exposés pour que les
    // composants d'affichage (graphique de projection, cartes projets, gains
    // d'actions) formatent les montants BRUTS dans la BONNE devise au lieu de
    // retomber sur un « € » codé en dur.
    currency: { code: profile.currency, locale: profile.locale },
    formatMoney: money,
    userProfile: {
      firstName,
      lastName,
      age,
      city,
      children,
      monthlyIncome,
      monthlyExpenses,
      netWorth: patrimoineNetValue,
      cashflow: monthlyCashflow,
      hasInvestmentProfile: risqueScore != null && financiereScore != null,
      hasObjectives: objectifsDefinis.length > 0,
      risqueScore,
      financiereScore,
      financeInterests,
      isCashflowTheoretical, // LAP-748
    },
    patrimoineScore: {
      global: globalScore,
      grade,
      trend: '0', // TODO backend : exposer le delta vs mois dernier
      percentile: estimatePercentile(globalScore),
      dimensions,
    },
    diagnostic,
    actionsPrioritaires,
    projetsSugeres,
    projection20ans,
    objectivePlans,
    suggestedPrompts,
    // LAP-748 — Indicateurs de complétude, tolérants à plusieurs nominations
    // backend. Le bandeau "Complétez votre profil" affiche un CTA UNIQUEMENT
    // pour chaque champ vraiment manquant ; si tout est rempli, le bandeau
    // disparaît entièrement.
    completeness: {
      hasPortrait: !!firstName && !!age,
      // Le profil investisseur est considéré rempli si l'un des scores est
      // présent OU si des centres d'intérêts ont été sélectionnés (ce qui
      // implique que le questionnaire a été parcouru).
      hasProfilInvestisseur:
        risqueScore != null ||
        financiereScore != null ||
        (profil.risque !== undefined && profil.risque !== null) ||
        (profil.risk_score !== undefined && profil.risk_score !== null) ||
        financeInterests.length > 0,
      hasFinanceInterests: financeInterests.length > 0,
      hasBudget: monthlyIncome > 0,
      // Le patrimoine est considéré rempli dès qu'au moins UNE ligne d'actif
      // existe (même montant 0€). Le user a rempli sa card, c'est ce qui compte.
      hasPatrimoine:
        totalActifs > 0 ||
        (Array.isArray(actifs.labels) && actifs.labels.length > 0) ||
        (Array.isArray(actifs.data) && actifs.data.length > 0),
      hasObjectifs: objectifsDefinis.length > 0,
    },
  }
}

// ──────────────────────────────────────────────────────────────────
// Helpers privés
// ──────────────────────────────────────────────────────────────────

function num(v, fallback = 0) {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : fallback
}

function sum(arr) {
  if (!Array.isArray(arr)) return 0
  return arr.reduce((a, b) => a + (parseFloat(b) || 0), 0)
}

function computeAgeFromBirthdate(d) {
  if (!d) return null
  const birth = new Date(d)
  if (isNaN(birth.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  if (
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  ) {
    age--
  }
  return age
}

// IMPORTANT — format backend (cf. General.views.getMonPatrimoine) :
//   actifs.data  = POURCENTAGES de chaque ligne (la somme ≈ 100)
//   actifs.total = montant ABSOLU en euros
// L'ancien code traitait `data[i]` comme des euros et divisait par `total`
// (euros) → `_absolute` contenait en réalité des pourcentages et les ratios
// étaient minuscules. Conséquences : épargne de précaution sous-évaluée,
// diversification toujours à 100, et détection « a déjà ce produit » faussée.
// On détecte donc le format pourcentage et on reconstruit les euros via total.
function buildRepartition(labels = [], data = [], total = 0) {
  const ratios = {}
  const absolute = {}
  const sumData = sum(data)
  const totalEuros = num(total, 0) || sumData
  if (!sumData || !totalEuros) {
    ratios._absolute = absolute
    return ratios
  }
  // `data` ressemble à des pourcentages si sa somme avoisine 100 et que le
  // total (euros) en diffère nettement. Sinon on garde la sémantique « euros ».
  const looksLikePercent =
    Math.abs(sumData - 100) < 1.5 && totalEuros > 101 && Math.abs(totalEuros - sumData) > 1.5
  ;(labels || []).forEach((label, i) => {
    const key = ASSET_LABELS[label] || slugify(label)
    const raw = parseFloat(data[i]) || 0
    const ratio = looksLikePercent ? raw / 100 : raw / totalEuros
    const euros = looksLikePercent ? (raw / 100) * totalEuros : raw
    ratios[key] = (ratios[key] || 0) + ratio
    absolute[key] = (absolute[key] || 0) + euros
  })
  ratios._absolute = absolute
  return ratios
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function statusForScore(score) {
  if (score >= 70) return 'good'
  if (score >= 40) return 'warning'
  return 'critical'
}

function scoreDiversificationFromRepartition(repartition) {
  const ratios = Object.entries(repartition)
    .filter(([k]) => !k.startsWith('_'))
    .map(([, v]) => v)
  if (ratios.length === 0) return 0
  const hhi = ratios.reduce((acc, v) => acc + v * v, 0)
  return Math.round((1 - hhi) * 100)
}

function scoreFiscaliteFromTaxRate({ lastTaxRate, annualIncome, hasPer, profile }) {
  // Si lastTaxRate fourni, on s'en sert. Sinon estimation par tranche (barème
  // du pays de résidence via le profil, plus uniquement la France).
  let tmi =
    lastTaxRate != null
      ? lastTaxRate / 100
      : profile
        ? profile.estimateTmi(annualIncome)
        : 0
  if (tmi < 0.11) return 80
  let s = 20
  if (hasPer) s += 30
  return Math.min(100, s)
}

function epargneDetail(t, liquidites, depensesMensuelles, product = '') {
  if (!depensesMensuelles) {
    return t('conseillerPatrimonial.dimensionDetails.epargne.noBudget')
  }
  const mois = liquidites / depensesMensuelles
  const months = mois.toFixed(1)
  if (mois >= 6) return t('conseillerPatrimonial.dimensionDetails.epargne.excellent', { months })
  if (mois >= 3) return t('conseillerPatrimonial.dimensionDetails.epargne.good', { months })
  if (mois >= 1)
    return t('conseillerPatrimonial.dimensionDetails.epargne.warning', { months })
  return t('conseillerPatrimonial.dimensionDetails.epargne.critical', { months, product })
}

function diversificationDetail(t, repartition) {
  const sorted = Object.entries(repartition)
    .filter(([k]) => !k.startsWith('_'))
    .sort(([, a], [, b]) => b - a)
  if (sorted.length === 0) {
    return t('conseillerPatrimonial.dimensionDetails.diversification.empty')
  }
  const [topKey, topPct] = sorted[0]
  if (topPct > 0.7) {
    return t('conseillerPatrimonial.dimensionDetails.diversification.concentrated', {
      pct: Math.round(topPct * 100),
      label: prettifyKey(t, topKey),
    })
  }
  if (sorted.length < 3) {
    if (sorted.length === 1)
      return t('conseillerPatrimonial.dimensionDetails.diversification.fewSingle')
    return t('conseillerPatrimonial.dimensionDetails.diversification.fewMultiple', {
      n: sorted.length,
    })
  }
  return t('conseillerPatrimonial.dimensionDetails.diversification.balanced', {
    n: sorted.length,
  })
}

function endettementDetail(t, money, patrimoineNet, cashflow) {
  if (patrimoineNet < 0) {
    return t('conseillerPatrimonial.dimensionDetails.endettement.negative', {
      amount: money(patrimoineNet),
    })
  }
  if (cashflow > 0) {
    return t('conseillerPatrimonial.dimensionDetails.endettement.positive', {
      amount: money(cashflow),
    })
  }
  return t('conseillerPatrimonial.dimensionDetails.endettement.weak', {
    amount: money(cashflow),
  })
}

function prevoyanceDetail(t, repartition, age, products = {}) {
  const hasPer = hasAny(repartition, RETIREMENT_KEYS)
  const hasAv = hasAny(repartition, AV_KEYS)
  const vars = {
    retirement: products.retirement || '',
    lifeInsurance: products.lifeInsurance || '',
  }
  if (hasPer && hasAv)
    return t('conseillerPatrimonial.dimensionDetails.prevoyance.perAndAv', vars)
  if (hasPer) return t('conseillerPatrimonial.dimensionDetails.prevoyance.onlyPer', vars)
  if (hasAv) return t('conseillerPatrimonial.dimensionDetails.prevoyance.onlyAv', vars)
  if (age && age < 35)
    return t('conseillerPatrimonial.dimensionDetails.prevoyance.youngNone', vars)
  return t('conseillerPatrimonial.dimensionDetails.prevoyance.none', vars)
}

function fiscaliteDetail(t, { lastTaxRate, annualIncome, hasPer, profile, products = {} }) {
  const tmi =
    lastTaxRate != null
      ? lastTaxRate
      : (profile ? profile.estimateTmi(annualIncome) : 0) * 100
  const vars = { tmi: tmi.toFixed(0), retirement: products.retirement || '' }
  if (tmi < 11) return t('conseillerPatrimonial.dimensionDetails.fiscalite.lowTmi', vars)
  if (hasPer) return t('conseillerPatrimonial.dimensionDetails.fiscalite.hasPer', vars)
  return t('conseillerPatrimonial.dimensionDetails.fiscalite.noPer', vars)
}

function objectifsDetail(t, objectifsDefinis) {
  if (objectifsDefinis.length === 0)
    return t('conseillerPatrimonial.dimensionDetails.objectifs.none')
  if (objectifsDefinis.length === 1)
    return t('conseillerPatrimonial.dimensionDetails.objectifs.definedSingle')
  return t('conseillerPatrimonial.dimensionDetails.objectifs.definedMultiple', {
    n: objectifsDefinis.length,
  })
}

function prettifyKey(t, key) {
  const translated = t(`conseillerPatrimonial.assetLabels.${key}`, { defaultValue: '' })
  if (translated) return translated
  return key.replace(/_/g, ' ')
}

function estimatePercentile(score) {
  // Approximation grossière : distribution normale centrée sur 50
  if (score >= 90) return 95
  if (score >= 80) return 85
  if (score >= 70) return 75
  if (score >= 55) return 60
  if (score >= 40) return 40
  if (score >= 25) return 22
  return 10
}

function buildDiagnostic({
  t,
  money,
  age,
  monthlyCashflow,
  monthlyIncome,
  patrimoineNetValue,
  repartition,
  liquiditesDisponibles,
  monthlyExpenses,
}) {
  const forces = []
  const faiblesses = []
  const risques = []

  // Forces
  if (monthlyCashflow > 0) {
    const rate = monthlyIncome ? Math.round((monthlyCashflow / monthlyIncome) * 100) : null
    forces.push({
      id: 'f-cashflow',
      title: t('conseillerPatrimonial.forces.cashflow.title'),
      description:
        rate != null
          ? t('conseillerPatrimonial.forces.cashflow.descWithRate', {
              amount: money(monthlyCashflow),
              rate,
            })
          : t('conseillerPatrimonial.forces.cashflow.descNoRate', {
              amount: money(monthlyCashflow),
            }),
      icon: 'TrendingUp',
    })
  }
  if (age && age < 35) {
    forces.push({
      id: 'f-jeune',
      title: t('conseillerPatrimonial.forces.young.title'),
      description: t('conseillerPatrimonial.forces.young.desc', { age }),
      icon: 'AccessTime',
    })
  }
  if (monthlyIncome > 4000) {
    forces.push({
      id: 'f-revenus',
      title: t('conseillerPatrimonial.forces.income.title'),
      description: t('conseillerPatrimonial.forces.income.desc', {
        amount: money(monthlyIncome),
      }),
      icon: 'AccountBalance',
    })
  }
  if (Object.keys(repartition).filter((k) => !k.startsWith('_')).length >= 3) {
    forces.push({
      id: 'f-diversification',
      title: t('conseillerPatrimonial.forces.diversification.title'),
      description: t('conseillerPatrimonial.forces.diversification.desc'),
      icon: 'PieChart',
    })
  }

  // Faiblesses
  if (patrimoineNetValue < 0) {
    faiblesses.push({
      id: 'w-net-negatif',
      title: t('conseillerPatrimonial.faiblesses.netNegative.title'),
      severity: 'high',
      description: t('conseillerPatrimonial.faiblesses.netNegative.desc', {
        amount: money(patrimoineNetValue),
      }),
      icon: 'TrendingDown',
    })
  }
  const compteCourantRatio = repartition.compte_courant || 0
  if (compteCourantRatio > 0.5) {
    faiblesses.push({
      id: 'w-compte-courant',
      title: t('conseillerPatrimonial.faiblesses.compteCourant.title'),
      severity: compteCourantRatio > 0.8 ? 'high' : 'medium',
      description: t('conseillerPatrimonial.faiblesses.compteCourant.desc', {
        pct: Math.round(compteCourantRatio * 100),
      }),
      icon: 'PieChart',
    })
  }
  if (liquiditesDisponibles < (monthlyExpenses || 0) * 3 && monthlyExpenses > 0) {
    faiblesses.push({
      id: 'w-epargne-precaution',
      title: t('conseillerPatrimonial.faiblesses.epargnePrecaution.title'),
      severity: 'high',
      description: t('conseillerPatrimonial.faiblesses.epargnePrecaution.desc', {
        liquid: money(liquiditesDisponibles),
        min: money(monthlyExpenses * 3),
        max: money(monthlyExpenses * 6),
      }),
      icon: 'Savings',
    })
  }

  // Risques
  if (liquiditesDisponibles < 2000) {
    risques.push({
      id: 'r-coup-dur',
      title: t('conseillerPatrimonial.risques.coupDur.title'),
      probability: 'high',
      impact: t('conseillerPatrimonial.risques.impactLevels.critique'),
      description: t('conseillerPatrimonial.risques.coupDur.desc', { amount: money(2000) }),
    })
  }
  if (compteCourantRatio > 0.5 && liquiditesDisponibles > 5000) {
    const erosion = Math.round(liquiditesDisponibles * 0.02)
    risques.push({
      id: 'r-inflation',
      title: t('conseillerPatrimonial.risques.inflation.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.certaine'),
      impact: t('conseillerPatrimonial.risques.impactLevels.moyen'),
      description: t('conseillerPatrimonial.risques.inflation.desc', {
        loss: money(erosion),
      }),
      annualLoss: erosion,
    })
  }
  if (
    age &&
    age >= 25 &&
    age < 60 &&
    sumAbsolute(repartition, [...RETIREMENT_KEYS, ...AV_KEYS]) === 0
  ) {
    risques.push({
      id: 'r-retraite',
      title: t('conseillerPatrimonial.risques.retraite.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.certaine'),
      impact: t('conseillerPatrimonial.risques.impactLevels.élevé'),
      description: t('conseillerPatrimonial.risques.retraite.desc'),
    })
  }

  // Garanties : au moins une force / faiblesse / risque pour ne pas avoir de colonne vide
  if (forces.length === 0) {
    forces.push({
      id: 'f-default',
      title: t('conseillerPatrimonial.forces.defaultEmpty.title'),
      description: t('conseillerPatrimonial.forces.defaultEmpty.desc'),
      icon: 'TrendingUp',
    })
  }
  if (faiblesses.length === 0) {
    faiblesses.push({
      id: 'w-default',
      title: t('conseillerPatrimonial.faiblesses.defaultOk.title'),
      severity: 'medium',
      description: t('conseillerPatrimonial.faiblesses.defaultOk.desc'),
      icon: 'TrendingUp',
    })
  }
  if (risques.length === 0) {
    risques.push({
      id: 'r-default',
      title: t('conseillerPatrimonial.risques.defaultOk.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.faible'),
      impact: t('conseillerPatrimonial.risques.impactLevels.faible'),
      description: t('conseillerPatrimonial.risques.defaultOk.desc'),
    })
  }

  return { forces, faiblesses, risques }
}

function buildActionsPrioritaires({
  t,
  money,
  profile,
  products = {},
  age,
  monthlyIncome,
  monthlyCashflow,
  lastTaxRate,
  repartition,
  liquiditesDisponibles,
  monthlyExpenses,
  objectifsDefinis,
  patrimoineNetValue,
}) {
  const actions = []
  const tmi =
    lastTaxRate != null
      ? lastTaxRate / 100
      : profile
        ? profile.estimateTmi(monthlyIncome * 12)
        : 0
  const hasPer = hasAny(repartition, RETIREMENT_KEYS)
  const hasAv = hasAny(repartition, AV_KEYS)
  const hasPea = hasAny(repartition, MARKET_KEYS)
  const hasLivret = hasAny(repartition, LIVRET_KEYS)

  // 1) Épargne de précaution
  const monthsCouverts = monthlyExpenses ? liquiditesDisponibles / monthlyExpenses : 0
  if (monthsCouverts < 3) {
    const cible = (monthlyExpenses || 1000) * 6
    const versementMensuel = Math.min(
      Math.max(Math.round(monthlyCashflow * 0.4), 100),
      Math.round(cible / 12),
    )
    actions.push({
      id: 'a-livret',
      rank: actions.length + 1,
      title: hasLivret
        ? t('conseillerPatrimonial.actionItems.livret.titleHasLivret', {
            amount: money(versementMensuel),
          })
        : t('conseillerPatrimonial.actionItems.livret.titleNoLivret', {
            amount: money(versementMensuel),
            product: products.precaution,
          }),
      category: t('conseillerPatrimonial.actionItems.categories.epargnePrecaution'),
      impact: 9,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.livret.horizonLabel'),
      gainAnnuel: Math.round(versementMensuel * 12 * 0.03),
      gainDescription: t('conseillerPatrimonial.actionItems.livret.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 12 }),
      explanation: t('conseillerPatrimonial.actionItems.livret.explanation', {
        product: products.precaution,
      }),
      cta: t('conseillerPatrimonial.actionItems.livret.cta'),
      projetId: 'p-livret',
      bestFor: [
        t('conseillerPatrimonial.actionItems.livret.bestFor1'),
        t('conseillerPatrimonial.actionItems.livret.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.livret.tag1'),
        t('conseillerPatrimonial.actionItems.livret.tag2'),
        t('conseillerPatrimonial.actionItems.livret.tag3'),
      ],
    })
  }

  // 2) PER si TMI ≥ 30% et pas de PER
  // Multi-pays : l'optimisation fiscale par l'épargne retraite n'a de sens que
  // dans les pays où le produit retraite est déductible (FR PER, DE Rürup,
  // BE EPP…). Là où il ne l'est pas (ex. retraite non déductible), on n'affiche
  // pas cette action — le chat LLM, lui, reste libre de nuancer.
  const retirementDeductible = !profile || profile.isTaxDeductibleRetirement()
  if (tmi >= 0.3 && !hasPer && monthlyCashflow > 100 && retirementDeductible) {
    const versementAnnuel = Math.min(8000, Math.round(monthlyCashflow * 12 * 0.4))
    const economie = Math.round(versementAnnuel * tmi)
    actions.push({
      id: 'a-per',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.per.title', {
        eco: money(economie),
        product: products.retirement,
      }),
      category: t('conseillerPatrimonial.actionItems.categories.optimisationFiscale'),
      impact: 8,
      facilite: 7,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.per.horizonLabel'),
      gainAnnuel: economie,
      gainDescription: t('conseillerPatrimonial.actionItems.per.gainDesc', {
        amount: money(versementAnnuel),
        tmi: Math.round(tmi * 100),
        product: products.retirement,
      }),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 15 }),
      explanation: t('conseillerPatrimonial.actionItems.per.explanation', {
        tmi: Math.round(tmi * 100),
        product: products.retirement,
      }),
      cta: t('conseillerPatrimonial.actionItems.per.cta', { product: products.retirement }),
      projetId: 'p-per',
      bestFor: [
        t('conseillerPatrimonial.actionItems.per.bestFor1'),
        t('conseillerPatrimonial.actionItems.per.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.per.tag1'),
        t('conseillerPatrimonial.actionItems.per.tag2'),
        t('conseillerPatrimonial.actionItems.per.tag3'),
      ],
    })
  }

  // 3) Assurance-vie si pas dispo
  if (!hasAv && monthlyCashflow > 50) {
    const versement = Math.max(Math.round(monthlyCashflow * 0.2), 100)
    actions.push({
      id: 'a-av',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.av.title', { product: products.lifeInsurance }),
      category: t('conseillerPatrimonial.actionItems.categories.diversification'),
      impact: 8,
      facilite: 8,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.av.horizonLabel'),
      gainAnnuel: Math.round(versement * 12 * 0.04),
      gainDescription: t('conseillerPatrimonial.actionItems.av.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 10 }),
      explanation: t('conseillerPatrimonial.actionItems.av.explanation', { product: products.lifeInsurance }),
      cta: t('conseillerPatrimonial.actionItems.av.cta', { product: products.lifeInsurance }),
      projetId: 'p-av',
      bestFor: [
        t('conseillerPatrimonial.actionItems.av.bestFor1'),
        t('conseillerPatrimonial.actionItems.av.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.av.tag1'),
        t('conseillerPatrimonial.actionItems.av.tag2'),
        t('conseillerPatrimonial.actionItems.av.tag3'),
      ],
    })
  }

  // 4) Virement automatique si cashflow > 0 et pas encore évoqué
  if (monthlyCashflow > 200) {
    const monthlyAmt = Math.round(monthlyCashflow * 0.5)
    actions.push({
      id: 'a-virement',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.virement.title', { amount: money(monthlyAmt) }),
      category: t('conseillerPatrimonial.actionItems.categories.comportement'),
      impact: 7,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.virement.horizonLabel'),
      gainAnnuel: monthlyAmt * 12,
      gainDescription: t('conseillerPatrimonial.actionItems.virement.gainDesc', {
        amount: money(monthlyAmt),
        result: money(Math.round(monthlyAmt * 12 * 10 * 1.22)),
      }),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 8 }),
      explanation: t('conseillerPatrimonial.actionItems.virement.explanation'),
      cta: t('conseillerPatrimonial.actionItems.virement.cta'),
      projetId: 'p-virement',
      bestFor: [
        t('conseillerPatrimonial.actionItems.virement.bestFor1'),
        t('conseillerPatrimonial.actionItems.virement.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.virement.tag1'),
        t('conseillerPatrimonial.actionItems.virement.tag2'),
        t('conseillerPatrimonial.actionItems.virement.tag3'),
      ],
    })
  }

  // 5) Achat RP si capacité d'emprunt (cashflow > 1000 et patrimoine non négatif)
  if (
    monthlyCashflow > 800 &&
    patrimoineNetValue >= -1000 &&
    (repartition._absolute?.immobilier || 0) === 0
  ) {
    const capaciteEmprunt = Math.round(monthlyIncome * 0.33 * 12 * 25)
    actions.push({
      id: 'a-immo',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.immo.title'),
      category: t('conseillerPatrimonial.actionItems.categories.immobilier'),
      impact: 9,
      facilite: 5,
      horizon: 'medium',
      horizonLabel: t('conseillerPatrimonial.actionItems.immo.horizonLabel'),
      gainAnnuel: Math.round(monthlyExpenses * 12 * 0.4),
      gainDescription: t('conseillerPatrimonial.actionItems.immo.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 18 }),
      explanation: t('conseillerPatrimonial.actionItems.immo.explanation', {
        amount: money(capaciteEmprunt),
      }),
      cta: t('conseillerPatrimonial.actionItems.immo.cta'),
      projetId: 'p-immo',
      bestFor: [
        t('conseillerPatrimonial.actionItems.immo.bestFor1'),
        t('conseillerPatrimonial.actionItems.immo.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.immo.tag1'),
        t('conseillerPatrimonial.actionItems.immo.tag2'),
        t('conseillerPatrimonial.actionItems.immo.tag3'),
      ],
    })
  }

  // 6) PEA pour croissance
  if (!hasPea && monthlyCashflow > 100 && age && age < 60) {
    const versement = Math.max(Math.round(monthlyCashflow * 0.15), 100)
    actions.push({
      id: 'a-pea',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.pea.title', {
        amount: money(versement),
        product: products.equity,
      }),
      category: t('conseillerPatrimonial.actionItems.categories.marches'),
      impact: 8,
      facilite: 7,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.pea.horizonLabel'),
      gainAnnuel: Math.round(versement * 12 * 0.07),
      gainDescription: t('conseillerPatrimonial.actionItems.pea.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 12 }),
      explanation: t('conseillerPatrimonial.actionItems.pea.explanation', { product: products.equity }),
      cta: t('conseillerPatrimonial.actionItems.pea.cta', { product: products.equity }),
      projetId: 'p-pea',
      bestFor: [
        t('conseillerPatrimonial.actionItems.pea.bestFor1'),
        t('conseillerPatrimonial.actionItems.pea.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.pea.tag1'),
        t('conseillerPatrimonial.actionItems.pea.tag2'),
        t('conseillerPatrimonial.actionItems.pea.tag3'),
      ],
    })
  }

  // 7) Définir des objectifs
  if (objectifsDefinis.length === 0) {
    actions.push({
      id: 'a-objectifs',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.objectifs.title'),
      category: t('conseillerPatrimonial.actionItems.categories.pilotage'),
      impact: 6,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.objectifs.horizonLabel'),
      gainAnnuel: 0,
      gainDescription: t('conseillerPatrimonial.actionItems.objectifs.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 8 }),
      explanation: t('conseillerPatrimonial.actionItems.objectifs.explanation'),
      cta: t('conseillerPatrimonial.actionItems.objectifs.cta'),
      projetId: 'p-objectifs',
      bestFor: [
        t('conseillerPatrimonial.actionItems.objectifs.bestFor1'),
        t('conseillerPatrimonial.actionItems.objectifs.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.objectifs.tag1'),
        t('conseillerPatrimonial.actionItems.objectifs.tag2'),
      ],
    })
  }

  return actions.slice(0, 7)
}

function buildProjets({ t, money, profile, products = {}, monthlyCashflow, repartition, age, monthlyExpenses, liquiditesDisponibles = 0, city }) {
  const projets = []
  const cashflow = Math.max(monthlyCashflow, 0)
  const monthlyExpensesSafe = monthlyExpenses || 1500

  // Épargne de précaution : on cible 6 mois de dépenses MAIS on tient compte de
  // ce que l'utilisateur a DÉJÀ de liquide. S'il détient déjà un livret/des
  // liquidités, le « reste à constituer » (gap) est ce qui pilote le versement
  // mensuel et la durée — sinon on lui recommandait de ré-épargner toute la
  // somme alors qu'« il a déjà un livret, il ne met juste pas assez dessus ».
  const precautionTarget = Math.round(monthlyExpensesSafe * 6)
  const precautionGap = Math.max(precautionTarget - Math.max(liquiditesDisponibles, 0), 0)
  // Montant à constituer pour le calcul d'effort (au moins 1 mois si déjà bien
  // doté, pour garder un projet actionnable et non nul).
  const precautionEffort = precautionGap > 0 ? precautionGap : Math.round(monthlyExpensesSafe)
  const precautionMonthly = Math.max(Math.round(cashflow * 0.3), 100)
  const durationMonths = Math.max(
    Math.round(precautionEffort / Math.max(precautionMonthly, 100)),
    1,
  )

  projets.push({
    id: 'p-livret',
    title: t('conseillerPatrimonial.projetItems.livret.title'),
    icon: 'Savings',
    targetAmount: precautionTarget,
    monthlyContribution: precautionMonthly,
    duration: t('conseillerPatrimonial.projetItems.livret.duration', { n: durationMonths }),
    expectedYield: t('conseillerPatrimonial.projetItems.livret.yield'),
    vehicle: t('conseillerPatrimonial.projetItems.livret.vehicle', { product: products.precaution }),
    pillar: t('conseillerPatrimonial.projetItems.livret.pillar'),
    color: '#22c55e',
    description: t('conseillerPatrimonial.projetItems.livret.description', {
      amount: money(precautionTarget),
      product: products.precaution,
    }),
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: Math.max(durationMonths, 12),
      monthly_amount: precautionMonthly,
      target_amount: precautionTarget,
      vehicles: ['livret_a', 'ldds'],
    },
  })

  if (cashflow > 200 && !hasAny(repartition, RETIREMENT_KEYS)) {
    const yearsToRetirement = age ? Math.max(62 - age, 5) : null
    projets.push({
      id: 'p-per',
      title: t('conseillerPatrimonial.projetItems.per.title', { product: products.retirement }),
      icon: 'Elderly',
      targetAmount: 200000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.4), 200),
      duration: yearsToRetirement
        ? t('conseillerPatrimonial.projetItems.per.durationYears', { n: yearsToRetirement })
        : t('conseillerPatrimonial.projetItems.per.durationDefault'),
      expectedYield: t('conseillerPatrimonial.projetItems.per.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.per.vehicle', { product: products.retirement }),
      pillar: t('conseillerPatrimonial.projetItems.per.pillar'),
      color: '#8b5cf6',
      description: t('conseillerPatrimonial.projetItems.per.description', { product: products.retirement }),
      autofilled: {
        objectiveType: 'secondaire_fixe',
        duration_months: age ? Math.max((62 - age) * 12, 60) : 300,
        monthly_amount: Math.max(Math.round(cashflow * 0.4), 200),
        target_amount: 200000,
        vehicles: ['per_individuel'],
        tax_optimized: true,
      },
    })
  }

  if (cashflow > 100 && !hasAny(repartition, AV_KEYS)) {
    projets.push({
      id: 'p-av',
      title: t('conseillerPatrimonial.projetItems.av.title', { product: products.lifeInsurance }),
      icon: 'Shield',
      targetAmount: 50000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.2), 100),
      duration: t('conseillerPatrimonial.projetItems.av.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.av.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.av.vehicle', { product: products.lifeInsurance }),
      pillar: t('conseillerPatrimonial.projetItems.av.pillar'),
      color: '#0ea5e9',
      description: t('conseillerPatrimonial.projetItems.av.description', { product: products.lifeInsurance }),
      autofilled: {
        objectiveType: 'secondaire_variable',
        duration_months: 96,
        monthly_amount: Math.max(Math.round(cashflow * 0.2), 100),
        vehicles: ['assurance_vie'],
      },
    })
  }

  if (cashflow > 800 && (repartition._absolute?.immobilier || 0) === 0) {
    // Prix cible d'une résidence principale : on part de la capacité d'épargne
    // (proxy d'emprunt) MAIS on l'ajuste au niveau RÉEL des prix du pays via
    // realEstateIndex (base FR = 1.0). Sinon on affichait un prix « français »
    // (ex. 284 k) pour une ville où l'immobilier est bien plus cher (Lausanne…).
    const reIndex = (profile && profile.realEstateIndex) || 1.0
    const immoTarget = Math.round(cashflow * 12 * 25 * 0.7 * reIndex)
    projets.push({
      id: 'p-immo',
      title: city
        ? t('conseillerPatrimonial.projetItems.immo.titleWithCity', { city })
        : t('conseillerPatrimonial.projetItems.immo.titleNoCity'),
      icon: 'Home',
      targetAmount: immoTarget,
      monthlyContribution: Math.round(cashflow * 0.6),
      duration: t('conseillerPatrimonial.projetItems.immo.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.immo.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.immo.vehicle'),
      pillar: t('conseillerPatrimonial.projetItems.immo.pillar'),
      color: '#f59e0b',
      description: t('conseillerPatrimonial.projetItems.immo.description'),
      autofilled: {
        objectiveType: 'necessaire_fixe',
        duration_months: 300,
        monthly_amount: Math.round(cashflow * 0.6),
        target_amount: immoTarget,
        vehicles: ['pret_immobilier'],
      },
    })
  }

  if (cashflow > 100 && age && age < 60 && !hasAny(repartition, MARKET_KEYS)) {
    projets.push({
      id: 'p-pea',
      title: t('conseillerPatrimonial.projetItems.pea.title', { product: products.equity }),
      icon: 'ShowChart',
      targetAmount: 80000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.15), 100),
      duration: t('conseillerPatrimonial.projetItems.pea.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.pea.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.pea.vehicle', { product: products.equity }),
      pillar: t('conseillerPatrimonial.projetItems.pea.pillar'),
      color: '#ec4899',
      description: t('conseillerPatrimonial.projetItems.pea.description', { product: products.equity }),
      autofilled: {
        objectiveType: 'secondaire_variable',
        duration_months: 180,
        monthly_amount: Math.max(Math.round(cashflow * 0.15), 100),
        vehicles: ['pea', 'etf_world'],
      },
    })
  }

  projets.push({
    id: 'p-prevoyance',
    title: t('conseillerPatrimonial.projetItems.prevoyance.title'),
    icon: 'Shield',
    targetAmount: 0,
    monthlyContribution: 35,
    duration: t('conseillerPatrimonial.projetItems.prevoyance.duration'),
    expectedYield: t('conseillerPatrimonial.projetItems.prevoyance.yield'),
    vehicle: t('conseillerPatrimonial.projetItems.prevoyance.vehicle'),
    pillar: t('conseillerPatrimonial.projetItems.prevoyance.pillar'),
    color: '#22c55e',
    description: t('conseillerPatrimonial.projetItems.prevoyance.description'),
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: 0,
      monthly_amount: 35,
      vehicles: ['prevoyance'],
    },
  })

  return projets.slice(0, 6)
}

function buildProjection20ans({ patrimoineNetValue, monthlyCashflow }) {
  const start = patrimoineNetValue || 0
  // Sans action : suit l'évolution actuelle (cashflow * 12 si positif, sinon stagne)
  const annualDelta = Math.max(0, (monthlyCashflow || 0) * 12 * 0.2) // 20% de l'épargne mis de côté en moyenne
  const actuel = []
  for (let y = 0; y <= 20; y++) {
    actuel.push({ year: 2026 + y, value: Math.round(start + annualDelta * y) })
  }
  // Avec actions : 80% de l'épargne investie à 4.5% capitalisés
  const annualContrib = Math.max(0, (monthlyCashflow || 0) * 12 * 0.8)
  const avecActions = []
  let val = start
  for (let y = 0; y <= 20; y++) {
    avecActions.push({ year: 2026 + y, value: Math.round(val) })
    val = val * 1.045 + annualContrib
  }
  return { actuel, avecActions }
}

// ───────────────────────────────────────────────────────────────────────────
// LAP-CP07 — Plan pour atteindre un OBJECTIF DE PATRIMOINE (montant + horizon).
//
// L'utilisateur peut définir dans « Mes Objectifs » une cible de patrimoine
// (ex. 10 M€ à 15 ans). Le moteur n'en tenait pas compte : il ne produisait
// qu'une projection générique à 20 ans. Ici on construit un vrai plan
// d'atterrissage :
//   - épargne mensuelle requise pour atteindre la cible à l'horizon,
//   - verdict honnête (atteignable / ambitieux / hors de portée),
//   - le MAXIMUM FAISABLE avec les moyens actuels + les leviers concrets pour
//     rendre la cible réaliste (épargner plus, repousser l'échéance, viser une
//     cible revue, ou un rendement plus dynamique),
//   - des jalons intermédiaires + des données de courbe pour le graphique.
//
// Hypothèse de rendement annuel dérivée du PROFIL DE RISQUE (cohérent avec les
// allocations Prudent/Équilibré/Dynamique/Offensif déjà utilisées dans
// MonPatrimoine). Équilibré = 4,5 % reste aligné sur buildProjection20ans.
// ───────────────────────────────────────────────────────────────────────────

const RISK_RETURNS = {
  prudent: 0.03,
  equilibre: 0.045,
  dynamique: 0.065,
  offensif: 0.08,
}

function stripAccentsLower(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

// Profil de risque → rendement annuel attendu (décimal) + label normalisé.
function expectedReturnFromRisk(profil = {}) {
  const raw =
    profil.risque_score ||
    profil.risque_label ||
    profil.risque ||
    profil.profil_risque ||
    null
  const norm = stripAccentsLower(raw)
  if (norm.includes('prudent')) return { rate: RISK_RETURNS.prudent, key: 'prudent' }
  if (norm.includes('offensif')) return { rate: RISK_RETURNS.offensif, key: 'offensif' }
  if (norm.includes('dynamique')) return { rate: RISK_RETURNS.dynamique, key: 'dynamique' }
  if (norm.includes('equilibr')) return { rate: RISK_RETURNS.equilibre, key: 'equilibre' }
  // Profil inconnu / non renseigné → hypothèse équilibrée par défaut.
  return { rate: RISK_RETURNS.equilibre, key: 'equilibre' }
}

// Valeur future d'un capital P avec versement annuel pmt (fin de période) à r%.
function futureValue(P, pmtAnnual, r, years) {
  if (years <= 0) return P
  if (!r) return P + pmtAnnual * years
  const factor = Math.pow(1 + r, years)
  return P * factor + pmtAnnual * ((factor - 1) / r)
}

// Versement annuel requis pour atteindre `target` depuis P en `years` à r%.
function requiredAnnualContribution(P, target, r, years) {
  if (years <= 0) return Infinity
  if (!r) return (target - P) / years
  const factor = Math.pow(1 + r, years)
  return ((target - P * factor) * r) / (factor - 1)
}

// Plus petit nombre d'années pour atteindre `target` avec versement annuel fixe.
function solveYearsToTarget(P, pmtAnnual, r, target, maxYears = 80) {
  for (let y = 1; y <= maxYears; y++) {
    if (futureValue(P, pmtAnnual, r, y) >= target) return y
  }
  return null
}

// Rendement annuel requis (recherche dichotomique) pour atteindre `target`.
function solveReturnToTarget(P, pmtAnnual, years, target, maxRate = 0.25) {
  if (futureValue(P, pmtAnnual, maxRate, years) < target) return null
  let lo = 0
  let hi = maxRate
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2
    if (futureValue(P, pmtAnnual, mid, years) >= target) hi = mid
    else lo = mid
  }
  return hi
}

// ───────────────────────────────────────────────────────────────────────────
// LAP-CP08 — Moteur MULTI-OBJECTIFS. Étend le plan patrimoine (LAP-CP07) à
// tous les types d'objectifs définis dans « Mes Objectifs » :
//   - wealth         : montant de patrimoine cible à un horizon,
//   - income         : revenu mensuel / trimestriel / annuel,
//   - cashflow       : idem, mais comparé au flux de trésorerie courant,
//   - passive_income : capital nécessaire pour générer la rente (taux de
//                      distribution PARAMÉTRABLE côté UI),
//   - yield          : rendement cible des investissements vs profil de risque.
// Le moteur renvoie un TABLEAU de plans ; l'affichage les regroupe dans une
// seule section (PlanObjectifs).
// ───────────────────────────────────────────────────────────────────────────

const DEFAULT_DISTRIBUTION_RATE = 0.04 // 4 % — taux de distribution par défaut

// Tokens de détection par type d'objectif (multilingue, sans accents/casse).
const KIND_TOKENS = {
  passive_income: [
    'passive income',
    'revenu passif',
    'revenus passifs',
    'revenu passive', // tolère la faute de frappe FR fréquente
    'passif', // « revenu passif », « rente passive »…
    'passive',
    'passiv', // de
    'pasivo', // es
    'passivo', // it/pt
    'passives einkommen',
    'ingreso pasivo',
    'reddito passivo',
    'rente',
    'rentes',
  ],
  yield: [
    'rendement',
    'yield',
    'rentabilite',
    'rendite',
    'rendimiento',
    'rendimento',
    'return on',
    'taux de rendement',
    'roi',
  ],
  // Cibles de BUDGET (dépenses/épargne mensuelles), pas des objectifs de
  // croissance : nécessaire/secondaire (méthode 50/30/20), épargne, loisirs…
  // Détectées par le NOM/DOMAINE — JAMAIS par l'unité « % » (cf. plus bas).
  budget: [
    'necessaire',
    'secondaire',
    'epargne',
    'loisir',
    'depense',
    'depenses',
    'charges',
    'besoin',
    'envie',
    'budget',
    'frais',
    'expense',
    'spending',
  ],
  wealth: [
    'patrimoine',
    'patrimony',
    'wealth',
    'vermogen', // de (Vermögen)
    'patrimonio', // es/it
    'capital',
    'fortune',
    'net worth',
  ],
  cashflow: ['cashflow', 'cash flow', 'flux de tresorerie', 'tresorerie', 'flux net'],
  income: [
    'revenu',
    'revenue',
    'income',
    'salaire',
    'einkommen',
    'ingreso',
    'reddito',
    'gehalt',
  ],
}

// Détecte le TYPE d'objectif d'après son DOMAINE et son NOM (jamais l'unité,
// car l'unité par défaut est « % » et ferait passer tout objectif budgétaire
// pour un rendement).
//
// Le NOM prime sur le DOMAINE : dans les données réelles, les lignes de budget
// (« Nécessaire fixe », « Épargne »…) sont rangées sous un domaine générique
// « Rendement ». On teste donc d'abord budget/passif/patrimoine sur le NOM
// SEUL ; ce n'est qu'ensuite qu'on retombe sur le haystack complet (domaine
// inclus) pour le rendement, le cashflow et le revenu.
//
// Ordre de priorité : passive_income → budget → wealth (nom) →
//   passive_income → budget → yield → wealth → cashflow → income (haystack).
function detectObjectiveKind(o) {
  const name = stripAccentsLower(`${o.objectives_name || ''} ${o.name || ''}`)
  const haystack = stripAccentsLower(
    `${o.domain || ''} ${o.objectives_name || ''} ${o.name || ''}`,
  )
  const matches = (kind, hay) => KIND_TOKENS[kind].some((tok) => hay.includes(tok))

  // 1) Sur le NOM seul : ces types sont sans ambiguïté et doivent l'emporter
  //    même quand le domaine dit autre chose (ex. budget sous domaine
  //    « Rendement »).
  const namePriority = ['passive_income', 'budget', 'wealth']
  for (const kind of namePriority) {
    if (matches(kind, name)) return kind
  }
  // 2) Sinon, détection complète (domaine + nom).
  const order = ['passive_income', 'budget', 'yield', 'wealth', 'cashflow', 'income']
  for (const kind of order) {
    if (matches(kind, haystack)) return kind
  }
  // Plus de repli « % → rendement » : une unité en pourcentage ne suffit pas
  // à qualifier un rendement (le budget utilise aussi « % »). Sans mot-clé
  // exploitable, l'objectif n'est pas planifiable → non affiché.
  return null
}

// Périodicité → nombre d'occurrences par an + clé i18n ('month'|'quarter'|'year').
function normalizePeriodicity(periodicity) {
  const p = stripAccentsLower(periodicity)
  if (p.includes('trimestr') || p.includes('quarter'))
    return { perYear: 4, key: 'quarter' }
  if (
    p.includes('annuel') ||
    p.includes('annual') ||
    p.includes('yearly') ||
    p.includes('year') ||
    p === 'an'
  )
    return { perYear: 1, key: 'year' }
  // mensuel / monthly / mois / month / défaut
  return { perYear: 12, key: 'month' }
}

// Profil de risque suggéré pour atteindre un rendement cible (en %).
function suggestProfileForReturn(targetRatePct) {
  const target = Number(targetRatePct) / 100
  const order = ['prudent', 'equilibre', 'dynamique', 'offensif']
  for (const key of order) {
    if (RISK_RETURNS[key] >= target) return key
  }
  return 'offensif' // au-delà du plus offensif : exige (au moins) ce profil
}

function formatDateLabel(dateStr, locale) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return d.toLocaleDateString(locale || 'fr-FR')
  } catch (e) {
    return d.toISOString().slice(0, 10)
  }
}

// Plan d'accumulation PUR : depuis `start`, atteindre `target` en `horizonYears`
// à `rate` (décimal), avec une épargne mensuelle actuelle `currentMonthly`.
// EXPORTÉ pour permettre un recalcul live (ex. curseur de taux de distribution
// du revenu passif) sans repasser par tout le moteur.
export function computeAccumulationPlan(
  start,
  target,
  rate,
  horizonYears,
  currentMonthly,
) {
  const startVal = Math.round(Number(start) || 0)
  const targetAmount = Math.round(Number(target) || 0)
  const years = Math.max(1, Math.round(Number(horizonYears) || 1))
  const monthly = Math.max(Math.round(Number(currentMonthly) || 0), 0)
  const currentAnnual = monthly * 12

  // Trajectoire avec l'épargne actuelle entièrement investie au rendement r.
  const projectedWithCurrent = Math.round(
    futureValue(startVal, currentAnnual, rate, years),
  )
  const gap = targetAmount - projectedWithCurrent // >0 : il manque

  // Épargne requise pour atteindre pile la cible à l'horizon.
  const requiredAnnualRaw = requiredAnnualContribution(
    startVal,
    targetAmount,
    rate,
    years,
  )
  const requiredMonthly = Math.max(Math.round(requiredAnnualRaw / 12), 0)
  const extraMonthlyNeeded = Math.max(requiredMonthly - monthly, 0)

  // Verdict honnête fondé sur l'écart entre l'effort requis et l'épargne réelle.
  let verdict
  if (requiredMonthly <= monthly) verdict = 'reachable'
  else if (monthly <= 0) verdict = 'unreachable'
  else {
    const ratio = requiredMonthly / Math.max(monthly, 1)
    if (ratio <= 1.5) verdict = 'tight'
    else if (ratio <= 3) verdict = 'hard'
    else verdict = 'unreachable'
  }
  const feasible = verdict === 'reachable'

  // « Maximum faisable » + leviers pour rendre la cible réaliste (quand gap>0).
  const realisticHorizonYears =
    gap > 0 ? solveYearsToTarget(startVal, currentAnnual, rate, targetAmount) : years
  const requiredReturn =
    gap > 0 ? solveReturnToTarget(startVal, currentAnnual, years, targetAmount) : rate

  // Jalons le long de la trajectoire avec l'épargne actuelle (le réalisable).
  const markYears = Array.from(
    new Set([5, 10, 15, years].filter((y) => y >= 1 && y <= years)),
  ).sort((a, b) => a - b)
  const milestones = markYears.map((y) => ({
    years: y,
    value: Math.round(futureValue(startVal, currentAnnual, rate, y)),
  }))

  // Données de courbe : tendance actuelle vs trajectoire requise (si gap>0).
  const currentYear = new Date().getFullYear()
  const chart = []
  for (let y = 0; y <= years; y++) {
    chart.push({
      year: currentYear + y,
      tendance: Math.round(futureValue(startVal, currentAnnual, rate, y)),
      requis:
        gap > 0
          ? Math.round(futureValue(startVal, Math.max(requiredAnnualRaw, 0), rate, y))
          : null,
    })
  }

  return {
    currentNetWorth: startVal,
    currentMonthlySavings: monthly,
    requiredMonthlySavings: requiredMonthly,
    extraMonthlyNeeded,
    projectedWithCurrent,
    gap, // >0 : écart ; <=0 : marge
    feasible,
    verdict, // 'reachable' | 'tight' | 'hard' | 'unreachable'
    realisticHorizonYears, // années pour y arriver avec l'épargne actuelle (ou null)
    realisticTarget: projectedWithCurrent, // cible atteignable à l'horizon donné
    requiredReturnPct:
      requiredReturn != null ? Math.round(requiredReturn * 1000) / 10 : null,
    milestones,
    chart,
  }
}

function horizonYearsFromDate(dateStr, fallback = 10) {
  if (!dateStr) return fallback
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return fallback
  const years = (d.getTime() - Date.now()) / (365.25 * 24 * 3600 * 1000)
  if (!Number.isFinite(years) || years <= 0) return fallback
  return Math.max(1, Math.round(years))
}

// Construit le TABLEAU des plans, un par objectif exploitable (montant > 0 et
// type détecté). Chaque plan porte un champ `kind` qui pilote l'affichage.
function buildObjectivePlans({
  profile,
  objectifsDefinis = [],
  patrimoineNetValue = 0,
  monthlyCashflow = 0,
  monthlyIncome = 0,
  profil = {},
}) {
  const { rate, key: riskKey } = expectedReturnFromRisk(profil)
  const expectedReturnPct = Math.round(rate * 1000) / 10 // ex. 4.5
  const start = Math.round(patrimoineNetValue || 0)
  const currentMonthlySavings = Math.max(Math.round(monthlyCashflow || 0), 0)
  const locale = profile?.locale || 'fr-FR'

  const plans = []

  objectifsDefinis.forEach((o) => {
    if (!o || typeof o !== 'object') return
    const amount = Number(
      o.objectifs != null ? o.objectifs : o.montant != null ? o.montant : o.value,
    )
    if (!Number.isFinite(amount) || amount <= 0) return

    const kind = detectObjectiveKind(o)
    if (!kind) return

    const dateStr = o.date_echeance || o.date_objectives || o.date || null
    const horizonYears = horizonYearsFromDate(dateStr, kind === 'wealth' ? 15 : 10)
    const base = {
      id: o.id != null ? o.id : null,
      kind,
      objectiveName: o.objectives_name || o.name || '',
      horizonYears,
      targetDateLabel: formatDateLabel(dateStr, locale),
      riskKey,
      expectedReturnPct,
    }

    // ─── PATRIMOINE : montant cible, plan d'accumulation classique. ───
    if (kind === 'wealth') {
      const acc = computeAccumulationPlan(
        start,
        amount,
        rate,
        horizonYears,
        currentMonthlySavings,
      )
      plans.push({ ...base, targetAmount: Math.round(amount), ...acc })
      return
    }

    // ─── RENDEMENT : taux cible vs rendement du profil de risque. ───
    if (kind === 'yield') {
      const targetRatePct = Math.round(amount * 10) / 10
      const gapPct = Math.round((targetRatePct - expectedReturnPct) * 10) / 10
      const suggestedProfileKey = suggestProfileForReturn(targetRatePct)
      const maxProfileReturnPct = Math.round(RISK_RETURNS.offensif * 1000) / 10
      let verdict
      if (gapPct <= 0) verdict = 'reachable'
      else if (targetRatePct > maxProfileReturnPct) verdict = 'unreachable'
      else if (gapPct <= 2) verdict = 'tight'
      else verdict = 'hard'
      plans.push({
        ...base,
        targetRatePct,
        currentRatePct: expectedReturnPct,
        gapPct,
        suggestedProfileKey,
        maxProfileReturnPct,
        verdict,
      })
      return
    }

    // ─── Montants PÉRIODIQUES (budget / revenu / cashflow / revenu passif). ───
    const { perYear, key: periodicityKey } = normalizePeriodicity(o.periodicity)
    const targetPeriodic = Math.round(amount)
    const targetAnnual = Math.round(amount * perYear)
    const targetMonthly = Math.round(targetAnnual / 12)
    const distributionRate = DEFAULT_DISTRIBUTION_RATE
    const distributionRatePct = Math.round(distributionRate * 1000) / 10

    // ─── BUDGET : simple cible de dépense/épargne mensuelle, EXPRIMÉE EN
    //     DEVISE, SANS conversion en capital ni rendement. On classe juste la
    //     ligne (épargne vs dépense) pour l'agréger dans la section budget. ───
    if (kind === 'budget') {
      const isSaving = /epargne|saving|spar/.test(
        stripAccentsLower(`${o.objectives_name || ''} ${o.name || ''} ${o.domain || ''}`),
      )
      // LAP-CP08-pt3 — Beaucoup d'objectifs de budget/épargne sont stockés en
      // POURCENTAGE (les objectifs prédéfinis sont créés avec unit='%'). Dans
      // ce cas, on affiche aussi la VALEUR en devise calculée sur le dernier
      // revenu de référence (revenu moyen). Si l'unité est déjà une devise, on
      // garde la valeur telle quelle, sans conversion.
      const unit = String(o.unit_of_measurement || '').trim()
      const isPercent = unit === '' || unit.includes('%') || /percent|pourcent|prozent/i.test(unit)
      const referenceIncome = Math.round(monthlyIncome || 0)
      const targetPct = isPercent ? targetPeriodic : null
      const targetCurrency =
        isPercent && referenceIncome > 0
          ? Math.round((targetPeriodic / 100) * referenceIncome)
          : targetPeriodic
      plans.push({
        ...base,
        periodicityKey,
        targetPeriodic,
        targetMonthly,
        targetAnnual,
        isSaving,
        isPercent,
        targetPct,
        targetCurrency,
        referenceIncome,
      })
      return
    }

    // ─── REVENU PASSIF : capital nécessaire pour générer la rente. ───
    if (kind === 'passive_income') {
      const requiredCapital = Math.round(targetAnnual / distributionRate)
      const currentAnnualPassive = Math.round(start * distributionRate)
      const currentMonthlyPassive = Math.round(currentAnnualPassive / 12)
      const capitalPlan = computeAccumulationPlan(
        start,
        requiredCapital,
        rate,
        horizonYears,
        currentMonthlySavings,
      )
      plans.push({
        ...base,
        periodicityKey,
        targetPeriodic,
        targetMonthly,
        targetAnnual,
        distributionRatePct,
        requiredCapital,
        currentMonthlyPassive,
        currentAnnualPassive,
        capitalPlan,
        verdict: capitalPlan.verdict,
      })
      return
    }

    // ─── REVENU / CASHFLOW : « les deux » → écart récurrent + leviers ET
    //     équivalent capital (plan d'accumulation pour le constituer). ───
    const currentMonthly =
      kind === 'cashflow'
        ? Math.max(Math.round(monthlyCashflow || 0), 0)
        : Math.max(Math.round(monthlyIncome || 0), 0)
    const gapMonthly = targetMonthly - currentMonthly
    const capitalEquivalent = Math.round(targetAnnual / distributionRate)
    const capitalPlan = computeAccumulationPlan(
      start,
      capitalEquivalent,
      rate,
      horizonYears,
      currentMonthlySavings,
    )
    let verdict
    if (gapMonthly <= 0) verdict = 'reachable'
    else if (currentMonthly <= 0) verdict = 'unreachable'
    else {
      const ratio = targetMonthly / Math.max(currentMonthly, 1)
      if (ratio <= 1.5) verdict = 'tight'
      else if (ratio <= 3) verdict = 'hard'
      else verdict = 'unreachable'
    }
    plans.push({
      ...base,
      periodicityKey,
      targetPeriodic,
      targetMonthly,
      targetAnnual,
      currentMonthly,
      gapMonthly,
      distributionRatePct,
      capitalEquivalent,
      capitalPlan,
      verdict,
    })
  })

  // Ordre d'affichage stable : patrimoine → revenu → cashflow → passif →
  // rendement → budget (le budget en dernier, regroupé dans sa propre section).
  const KIND_ORDER = {
    wealth: 0,
    income: 1,
    cashflow: 2,
    passive_income: 3,
    yield: 4,
    budget: 5,
  }
  plans.sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9))
  return plans
}

function buildSuggestedPrompts({ t, money, products = {}, objectifsDefinis, age, repartition, monthlyCashflow }) {
  const prompts = []
  if (monthlyCashflow > 0) {
    prompts.push(
      t('conseillerPatrimonial.suggestedPrompts.canSave', {
        amount: money(monthlyCashflow),
      }),
    )
  }
  prompts.push(t('conseillerPatrimonial.suggestedPrompts.increaseScore'))
  if (!hasAny(repartition, RETIREMENT_KEYS)) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.openPer', { product: products.retirement }))
  }
  if ((repartition._absolute?.immobilier || 0) === 0 && monthlyCashflow > 500) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.buyHome'))
  }
  if (age && age < 35) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.strategyForAge'))
  }
  if (objectifsDefinis.length === 0) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.prioritizeObjectives'))
  }
  return prompts.slice(0, 5)
}

// completenessScore retiré : on raisonne maintenant champ par champ
// directement via les booléens has* exposés dans le retour.
